import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { practicalInformationApi } from 'src/Services/API';
import {
  CreatePracticalInformationDto,
  PracticalInformationDetailDto,
} from 'src/Services/API/generated';
import { trackEvent } from 'src/Services/Matomo';
import { trackPracticalInfoReminderTimeSet } from 'src/Services/Mixpanel/events';
import { ApiError } from 'src/types/api-error.types';

type UpdatePracticalInformationParams = {
  practicalInformationId: string;
  data: CreatePracticalInformationDto;
};

export const useUpdatePracticalInformation = (
  options?: UseMutationOptions<
    PracticalInformationDetailDto,
    ApiError,
    UpdatePracticalInformationParams
  >
) => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();

  return useMutation<PracticalInformationDetailDto, ApiError, UpdatePracticalInformationParams>(
    ['update-practical-information', assetId],
    async ({ practicalInformationId, data }) => {
      if (!assetId) {
        throw new Error('No asset selected');
      }

      const response = await practicalInformationApi.practicalInformationControllerUpdate(
        practicalInformationId,
        assetId,
        {
          ...data,
        }
      );

      return response.data;
    },
    {
      ...options,
      onSuccess: (
        data: PracticalInformationDetailDto,
        variables: UpdatePracticalInformationParams,
        context: unknown
      ) => {
        queryClient.invalidateQueries({ queryKey: ['fetch-all-practical-information'] });
        queryClient.invalidateQueries({
          queryKey: ['fetch-practical-information', assetId, data.id],
        });
        if (options?.onSuccess) {
          options.onSuccess(data, variables, context);
        }

        trackEvent('PracticalInformation', 'update', variables.data.title);

        if (data.reminderTimeOfDay) {
          trackPracticalInfoReminderTimeSet({
            practical_information_type: data.type,
            reminder_time_of_day: data.reminderTimeOfDay,
          });
        }

        queryClient.invalidateQueries({ queryKey: ['fetch-events'] });
        queryClient.invalidateQueries({ queryKey: ['fetch-event', data.id] });
      },
    }
  );
};
